import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';

type RsvpStatus = 'going' | 'interested' | null;

type Props = {
  runId: string;
  runDate: string; // YYYY-MM-DD
  clubId: string;
  runInstanceId?: string | null;
  fullWidth?: boolean;
};

export default function RsvpButton({
  runId,
  runDate,
  clubId,
  runInstanceId,
  fullWidth = false,
}: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [myStatus, setMyStatus] = useState<RsvpStatus>(null);
  const [goingCount, setGoingCount] = useState(0);
  const [interestedCount, setInterestedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchStatus = useCallback(async () => {
    const [goingResult, interestedResult, mineResult] = await Promise.all([
      supabase
        .from('run_rsvps')
        .select('id', { count: 'exact', head: true })
        .eq('run_id', runId)
        .eq('run_date', runDate)
        .eq('status', 'going'),

      supabase
        .from('run_rsvps')
        .select('id', { count: 'exact', head: true })
        .eq('run_id', runId)
        .eq('run_date', runDate)
        .eq('status', 'interested'),

      user
        ? supabase
            .from('run_rsvps')
            .select('status')
            .eq('run_id', runId)
            .eq('run_date', runDate)
            .eq('user_id', user.id)
            .maybeSingle()
        : Promise.resolve({ data: null }),
    ]);

    setGoingCount(goingResult.count ?? 0);
    setInterestedCount(interestedResult.count ?? 0);

    const mine = (mineResult as { data: { status: string } | null }).data;
    setMyStatus(mine ? (mine.status as RsvpStatus) : null);
    setLoading(false);
  }, [runId, runDate, user]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleClick = async (tappedStatus: 'going' | 'interested') => {
    if (!user) {
      navigate('/auth');
      return;
    }

    setSubmitting(true);

    if (myStatus === tappedStatus) {
      // Already this status — remove RSVP entirely
      await supabase
        .from('run_rsvps')
        .delete()
        .eq('run_id', runId)
        .eq('run_date', runDate)
        .eq('user_id', user.id);
    } else if (myStatus !== null) {
      // Switch from one status to the other
      await supabase
        .from('run_rsvps')
        .update({ status: tappedStatus })
        .eq('run_id', runId)
        .eq('run_date', runDate)
        .eq('user_id', user.id);
    } else {
      // No existing RSVP — insert new
      await supabase.from('run_rsvps').insert({
        run_id: runId,
        run_date: runDate,
        user_id: user.id,
        club_id: clubId,
        status: tappedStatus,
        run_instance_id: runInstanceId ?? null,
      });
    }

    await fetchStatus();
    setSubmitting(false);
  };

  if (loading) return null;

  const totalCount = goingCount + interestedCount;
  const attendingHref = `/clubs/${clubId}/runs/${runId}/attending?date=${runDate}`;

  const btnBase = fullWidth
    ? 'flex-1 inline-flex items-center justify-center rounded-xl py-2 text-xs font-semibold transition disabled:opacity-50'
    : 'inline-flex items-center justify-center rounded-xl px-4 py-2 text-xs font-semibold transition disabled:opacity-50';

  return (
    <div className={fullWidth ? 'flex flex-col gap-1.5' : 'inline-flex flex-col gap-1.5 self-start'}>
      {/* Going | Interested buttons */}
      <div className={fullWidth ? 'flex gap-2' : 'flex gap-2'}>
        <button
          type="button"
          onClick={() => handleClick('going')}
          disabled={submitting}
          className={`${btnBase} ${
            myStatus === 'going'
              ? 'bg-[#22c55e] text-white hover:bg-[#16a34a]'
              : 'border border-green-200 bg-[#dcfce7] text-green-800 hover:bg-[#bbf7d0]'
          }`}
        >
          {myStatus === 'going' ? '✓ Going' : 'Going'}
        </button>

        <button
          type="button"
          onClick={() => handleClick('interested')}
          disabled={submitting}
          className={`${btnBase} ${
            myStatus === 'interested'
              ? 'bg-[#ffde59] text-black hover:bg-[#f5d000]'
              : 'border border-yellow-200 bg-[#fefce8] text-yellow-800 hover:bg-[#fef9c3]'
          }`}
        >
          {myStatus === 'interested' ? '★ Interested' : 'Interested'}
        </button>
      </div>

      {/* Tappable count */}
      {totalCount > 0 && (
        <Link
          to={attendingHref}
          className="text-center text-xs text-black/45 underline-offset-2 hover:text-black hover:underline"
        >
          {goingCount > 0 && `${goingCount} going`}
          {goingCount > 0 && interestedCount > 0 && ' · '}
          {interestedCount > 0 && `${interestedCount} interested`}
        </Link>
      )}
    </div>
  );
}
